import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { upsertCartItem } from "../../../server/CollectSite";
import { guardCustomerAction } from "../../../utils/emailVerification";
import { canUseCustomerFeatures } from "../../../utils/userMode";
import { getMaxStockQty, notifyStockLimit } from "../../../utils/stockLimit";
import {
  PACKAGE_LINE_KIND,
  capPackageLineDisplayQty,
  getCartLineUpsertPromoId,
  getPackageLineDisplayQty,
  totalQuantityForPackageLineDisplay,
} from "../../../utils/packageCartLines";
import { ShoppingCart, Trash2 } from "lucide-react";

const ProductAddToCart = ({
  element,
  haveInfo,
  cardOverlay = false,
  retailCard = false,
  onRequestRemove = undefined,
  lineKind = PACKAGE_LINE_KIND.UNIT,
}) => {
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user);
  const isClientView = useSelector((state) => state.category.isClientView);
  const productsInCart = useSelector(
    (state) => state.productsInCart.productsInCart,
  );
  const { role, stationId } = userState;
  const customerFeatures = canUseCustomerFeatures(
    role,
    isClientView,
    userState.user,
  );

  const productId = element.productId;
  const inCart = productsInCart.find((item) => item.productId === productId);
  const nestedProduct = inCart?.product || element.product || element;
  const catalogFallback = element.pricing
    ? element
    : element.product || nestedProduct;
  const productForPackage = {
    ...nestedProduct,
    pricing: nestedProduct.pricing?.promotions?.length
      ? nestedProduct.pricing
      : catalogFallback?.pricing || nestedProduct.pricing,
  };
  const cartItem = inCart
    ? { ...inCart, product: productForPackage }
    : { productId, quantity: 0, product: productForPackage };
  const displayQty = getPackageLineDisplayQty(cartItem, lineKind);
  const isSyncing = useSelector(
    (state) => !!state.productsInCart.syncingByProductId?.[productId],
  );
  const [inputValue, setInputValue] = useState(displayQty);
  // Show the stock message once at max, then disable the + button.
  const [maxNotified, setMaxNotified] = useState(false);
  // Synchronous guard: state is async, so rapid clicks would re-fire the alert
  // while it animates open (Swal.isVisible() is briefly false) — this fires it once.
  const maxNotifiedRef = useRef(false);
  const qtyRef = useRef(displayQty);

  useEffect(() => {
    setInputValue(displayQty);
    qtyRef.current = displayQty;
  }, [displayQty]);

  const catalogProduct = productForPackage;

  const callUpsertCartItem = (productObj, quantity, stationId) => {
    if (guardCustomerAction(dispatch, { user: userState })) {
      return;
    }
    const finalPrice = parseFloat(productObj.pricing.price).toFixed(2);
    const promoId = getCartLineUpsertPromoId(productObj);
    dispatch(
      upsertCartItem(
        productId,
        finalPrice,
        quantity,
        String(stationId),
        promoId,
        productObj,
      ),
    );
  };

  const applyDisplayQty = (nextDisplayQty) => {
    const nextTotal = totalQuantityForPackageLineDisplay(
      cartItem,
      lineKind,
      nextDisplayQty,
    );
    if (nextTotal === (inCart?.quantity || 0)) return;
    callUpsertCartItem(catalogProduct, nextTotal, stationId);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setInputValue(value === "" ? "" : parseInt(value));
    }
  };

  const handleInputBlur = () => {
    const raw = parseInt(inputValue, 10) || 0;
    let nextDisplay =
      raw <= 0
        ? 0
        : Math.max(displayStep, Math.round(raw / displayStep) * displayStep);

    const capped = capPackageLineDisplayQty(
      cartItem,
      lineKind,
      nextDisplay,
      maxQty,
    );
    if (capped !== nextDisplay) {
      notifyStockLimit(maxQty);
      nextDisplay = capped;
    }

    applyDisplayQty(nextDisplay);
    setInputValue(displayQty);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const weightJump =
    parseInt(
      element?.weightJump ||
        element?.product?.weightJump ||
        inCart?.product?.weightJump,
    ) > 0
      ? parseInt(
          element?.weightJump ||
            element?.product?.weightJump ||
            inCart?.product?.weightJump,
        )
      : 1;

  const displayStep = lineKind === PACKAGE_LINE_KIND.BUNDLE ? 1 : weightJump;
  const showStepper = displayQty > 0;

  // Catalog products expose the flag on the root; cart lines nest it under product.
  const allowOrderWhenOos = !!(
    element?.allowOrderWhenOos ||
    element?.product?.allowOrderWhenOos ||
    inCart?.allowOrderWhenOos ||
    inCart?.product?.allowOrderWhenOos
  );

  // In the cart drawer, the lowest step shows a trash icon instead of "-".
  const isRemoveStep = retailCard && showStepper && displayQty <= displayStep;

  const maxQty = getMaxStockQty(
    element?.availability?.["availableQuantity"] ??
      catalogProduct?.availability?.["availableQuantity"] ??
      element?.product?.availability?.["availableQuantity"] ??
      inCart?.availability?.["availableQuantity"] ??
      inCart?.product?.availability?.["availableQuantity"],
    weightJump,
    allowOrderWhenOos,
  );

  const nextIncrementTotal = totalQuantityForPackageLineDisplay(
    cartItem,
    lineKind,
    displayQty + displayStep,
  );
  const atMax = nextIncrementTotal > maxQty;
  const incrementDisabled = (atMax && maxNotified) || isSyncing;

  useEffect(() => {
    if (!atMax) {
      setMaxNotified(false);
      maxNotifiedRef.current = false;
    }
  }, [atMax]);

  const handleIncrement = () => {
    if (isSyncing) return;
    const currentQty = qtyRef.current;
    const requestedDisplay =
      (Math.floor(currentQty / displayStep) + 1) * displayStep;
    const requestedTotal = totalQuantityForPackageLineDisplay(
      cartItem,
      lineKind,
      requestedDisplay,
    );
    if (requestedTotal > maxQty) {
      if (maxNotifiedRef.current) return;
      maxNotifiedRef.current = true;
      notifyStockLimit(maxQty);
      setMaxNotified(true);
      return;
    }
    applyDisplayQty(requestedDisplay);
  };

  const handleAdd = () => {
    applyDisplayQty(displayStep);
  };

  const handleDecrement = () => {
    const newDisplay = Math.floor((displayQty - 1) / displayStep) * displayStep;
    applyDisplayQty(Math.max(0, newDisplay));
  };

  return (
    <div
      className={`${showStepper ? "add-to-cart in-cart" : "add-to-cart not-in-cart"}${
        cardOverlay ? " add-to-cart--card-overlay" : ""
      }${retailCard ? " add-to-cart--retail-card" : ""}`}
    >
      {customerFeatures &&
      (allowOrderWhenOos ||
        (element.availability ?? catalogProduct.availability)?.[
          "availableQuantity"
        ] >= weightJump ||
        (inCart && inCart.quantity > 0)) ? (
        <div className="wrap" onClick={!showStepper ? handleAdd : null}>
          {showStepper ? (
            <>
              <div
                className={`add-btn${
                  incrementDisabled && !isSyncing ? " add-btn--at-max" : ""
                }${isSyncing ? " add-btn--syncing" : ""}`}
                onClick={incrementDisabled ? null : handleIncrement}
                aria-disabled={incrementDisabled}
                aria-busy={isSyncing || undefined}
                title={
                  isSyncing
                    ? "מעדכן כמות..."
                    : incrementDisabled
                      ? "הגעת למגבלת המלאי"
                      : undefined
                }
              >
                {isSyncing ? (
                  <span className="add-btn__spinner" aria-hidden />
                ) : (
                  <p>+</p>
                )}
              </div>
              <div className="col-lg-7">
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  inputMode="numeric"
                />
              </div>
              <div
                className={`minus-btn${isRemoveStep ? " minus-btn--remove" : ""}`}
                aria-label={isRemoveStep ? "הסר מהעגלה" : undefined}
                onClick={
                  isRemoveStep && onRequestRemove
                    ? () => onRequestRemove()
                    : showStepper
                      ? handleDecrement
                      : null
                }
              >
                {isRemoveStep ? <Trash2 size={16} aria-hidden /> : <p>-</p>}
              </div>
            </>
          ) : (
            <p
              className={retailCard ? "add-to-cart__cta-retail" : undefined}
              onClick={
                !showStepper &&
                (!haveInfo ||
                  (element.QuantityCanOrder ??
                    catalogProduct.QuantityCanOrder) >= weightJump)
                  ? handleAdd
                  : null
              }
            >
              <ShoppingCart
                size={retailCard ? 18 : cardOverlay ? 22 : 24}
                aria-hidden
              />
              {retailCard ? (
                <span className="add-to-cart__cta-label">הוסף לסל</span>
              ) : null}
            </p>
          )}
        </div>
      ) : (
        !allowOrderWhenOos &&
        inCart &&
        inCart?.quantity === 0 && <div className="outOfStock">אזל מהמלאי</div>
      )}
    </div>
  );
};

export default ProductAddToCart;
